import { TableContainer, Thead, Th, Box, Text, Flex } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import { AxiosResponse } from 'axios';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useApi } from '../../../../context/ApiProvider';
import { PaginationInterface } from '../../../../shared/type/paginationInterface.type';
import {
  TransactionHistoryLogInterface,
  TransactionHistoryInterface
} from '../../../../shared/type/transactionHistoryInterface.type';
import CustomChakraButton from '../../../CustomChakraButton/CustomChakraButton';
import { DataTable } from '../../../DataTable/DataTable';
import Pagination from '../../../Pagination/Pagination';
import AddCustomOperation from './AddCustomOperation/AddCustomOperation';
import { StickyScrollableTableContainer } from '../../../DataTable/StickyScrollableTableContainer';

interface Props {
  consumerId: number;
  userId: number;
}

const BalanceHistory = ({ consumerId, userId }: Props) => {
  const { apiConsumerFinancialHistoryController } = useApi();
  const { id: tenantId } = useParams();
  const columnHelper = createColumnHelper<TransactionHistoryLogInterface>();

  const [isLoading, setIsLoading] = useState(false);
  const [balanceHistory, setBalanceHistory] = useState<TransactionHistoryLogInterface[]>([]);
  const [parentUserId, setParentUserId] = useState('0');
  const [showAddOperation, setShowAddOperation] = useState(false);

  const [actualPage, setActualPage] = useState<PaginationInterface>({
    totalCount: 0,
    hasNextPage: false,
    next: { limit: 0, offset: 0 }
  });

  const [page, setPage] = useState({
    pageNumber: 1,
    pageSize: 30,
    maxPageNumber: 0
  });

  const fetchBalanceHistory = async () => {
    setIsLoading(true);
    try {
      const response: AxiosResponse<TransactionHistoryInterface> =
        await apiConsumerFinancialHistoryController('parent-transactions').get('', {
          params: { pageNumber: page.pageNumber, pageSize: page.pageSize, tenantId, parentUserId }
        });

      const { data, next, hasNextPage, totalCount } = response.data;
      setBalanceHistory(data);
      setActualPage({ next, hasNextPage, totalCount });
      setPage((prev) => ({ ...prev, maxPageNumber: Math.ceil(totalCount / page.pageSize) }));
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userId) setParentUserId(userId.toString());
  }, [userId]);

  useEffect(() => {
    if (Number(parentUserId) !== 0) fetchBalanceHistory();
  }, [parentUserId, page.pageNumber]);

  const handlePageChange = (pageNum: number) => {
    setPage((prev) => ({ ...prev, pageNumber: pageNum }));
  };

  const columns = [
    columnHelper.accessor('when', {
      header: 'Data operacji',
      size: 90,
      cell: (info) => (
        <p className="text-sm font-medium text-grayLight-900 w-[72px] text-wrap">{info.getValue()}</p>
      )
    }),
    columnHelper.accessor('description', {
      header: 'Opis operacji',
      cell: (info) => (
        <p className="text-sm font-medium text-grayLight-900 w-[250px] text-wrap">{info.getValue()}</p>
      )
    }),
    columnHelper.accessor('amount', {
      header: 'Kwota (zł)',
      size: 90,
      cell: (info) => (
        <p className="text-sm font-medium text-grayLight-900 w-[80px]">{info.getValue()}</p>
      )
    }),
    columnHelper.accessor('saldoBeforeOperation', {
      header: 'Saldo przed (zł)',
      size: 90,
      cell: (info) => (
        <p className="text-sm font-medium text-grayLight-900 w-[80px]">{info.getValue()}</p>
      )
    }),
    columnHelper.accessor('saldoAfterOperation', {
      header: 'Saldo po (zł)',
      size: 90,
      cell: (info) => (
        <p className="text-sm font-medium text-grayLight-900 w-[80px]">{info.getValue()}</p>
      )
    }),
    columnHelper.accessor('performedByUser.lastName', {
      header: 'Wykonana przez',
      cell: (info) => {
        const { lastName, firstName } = info.row.original.performedByUser;
        return (
          <p className="text-sm font-medium text-grayLight-900 w-44">
            {lastName} {firstName}
          </p>
        );
      }
    })
  ];

  return (
    <section className="p-spacing-xl w-full h-screen overflow-y-hidden">
      <div className="w-full mb-4 flex items-center justify-between">
        <div className="flex flex-col basis-4/5">
          <Box>
            <Text
              whiteSpace="normal"
              overflow="hidden"
              textOverflow="ellipsis"
              className="font-semibold text-base text-grayLight-900">
              Historia salda
            </Text>
          </Box>
        </div>
      </div>
      <StickyScrollableTableContainer
        columns={columns}
        data={balanceHistory}
        isLoading={isLoading}
        showClearFilters={false}
        filtersRightContent={
          <CustomChakraButton
            size="md"
            hierarchy="secondaryColor"
            iconPosition="left"
            icon="plus"
            buttonProps={{ type: 'button', onClick: () => setShowAddOperation(true) }}>
            Edycja salda
          </CustomChakraButton>
        }
      />

      <Box position="relative" pt={4} pb={6}>
        <Pagination
          pageNumber={page.pageNumber}
          maxPageNumber={page.maxPageNumber}
          hasNextPage={actualPage.hasNextPage}
          onPageChange={handlePageChange}
        />
      </Box>

      {showAddOperation && (
        <AddCustomOperation
          currentSaldo={balanceHistory[0]?.saldoAfterOperation}
          consumerId={consumerId}
          isOpen={showAddOperation}
          handleClose={() => {
            setShowAddOperation(false);
            fetchBalanceHistory();
          }}
        />
      )}
    </section>
  );
};

export default BalanceHistory;
